import { ChangeDetectorRef, Directive, Injector, Input, OnChanges, OnInit, Optional, SimpleChanges } from '@angular/core';
import { AbstractControl, NgControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Dropdown } from 'primeng/dropdown';
import { MultiSelect } from 'primeng/multiselect';
import { isNullOrEmpty } from '../../../util/src/lib/is-null-or-empty';

/**
 * 下拉選單元件設定
 * p-dropdown
 * @export
 * @class DropdownSettingDirective
 * @implements {OnChanges}
 */
@Directive({
  selector: '[sigvDropdownSetting]'
})
export class DropdownSettingDirective implements OnChanges, OnInit {

  /**
   * 目標元件(p-dropdown/p-multiSelect)
   */
  private currentWidget: any;

  /**
   * 可顯示的下拉選單選項
   */
  private displayedOptions: any[] = [];

  private _control: AbstractControl | null = null;

  private disabledValue: any;

  /**
   * 下拉選單選項
   */
  @Input() options: any[] = [];

  /**
   * 是否顯示停用選項
   */
  @Input() showDisabledOptions = false;

  constructor(
    private injector: Injector,
    private translateService: TranslateService,
    private cd: ChangeDetectorRef,
    @Optional() private dropdown: Dropdown,
    @Optional() private multiSelect: MultiSelect) {
    if (isNullOrEmpty(dropdown) === false) {
      this.currentWidget = dropdown;
    }

    if (isNullOrEmpty(multiSelect) === false) {
      this.currentWidget = multiSelect;
    }
  }

  get control(): AbstractControl | null {
    const ngControlDirective = this.injector.get(NgControl);
    const control = ngControlDirective.control as any | null;
    if (control) {
      if (control != this._control) {
        // 偵測傳入的 formControlName 值的變化
        control.valueChanges.subscribe({
          next: (result: any) => {
            if (this.options.findIndex(x => x.disabled && x.value === result) > -1) {
              this.disabledValue = result;
            }
            this.setupOptions();
          },
          error: (err: any) => err
        });
      }
      this._control = control;
    }

    return this._control;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options && changes.options.currentValue) {
      if (this.options.findIndex(x => x.disabled && x.value === this.control?.value) > -1) {
        this.disabledValue = this.control?.value;
      }
      this.setupOptions();
    }
  }

  ngOnInit(): void {
    if (isNullOrEmpty(this.currentWidget.appendTo)) {
      this.currentWidget.appendTo = 'body';
    }

    if (this.currentWidget instanceof Dropdown) {
      this.currentWidget.autoDisplayFirst = false;
      this.currentWidget.showClear = true;
      this.currentWidget.updateSelectedOption(this.currentWidget.value);
    }

    this.setPlaceholder();

    this.translateService.onLangChange
      .subscribe({
        next: (event: any) => {
          this.setPlaceholder();
          this.cd.detectChanges();
        }
      });
  }

  /**
   * 設定 placeholder
   *
   * @private
   * @memberof DropdownSettingDirective
   */
  private setPlaceholder(): void {
    const msg = this.translateService.instant('Please Select');
    this.currentWidget.placeholder = msg;
  }

  private setupOptions(): void {
    if (this.showDisabledOptions) {
      this.displayedOptions = this.options;
    } else {
      const ctrlValue = this.control?.value;
      this.displayedOptions = this.options.filter(x => !x.disabled
        || (x.disabled && this.checkOptionValueIsEqual(x, ctrlValue))
        || (x.disabled && x.value === this.disabledValue));
    }
    this.currentWidget.options = this.displayedOptions;
    this.currentWidget.filter = this.currentWidget.options.length > 10 ? true : false;
    this.currentWidget.filterValue = null;
  }

  private checkOptionValueIsEqual(optionObj: any, value: any): boolean {
    const optionValueKey = this.currentWidget.optionValue;
    const isEqual = (!optionValueKey ? optionObj.value : optionObj[optionValueKey]) === value;

    return isEqual;
  }
}
